import ApiModel                       from '@widesk-core/models/ApiModel';
import Axios                          from 'axios';
import { AxiosError }                 from 'axios';
import { AxiosInstance }              from 'axios';
import { AxiosResponse }              from 'axios';
import { Collection }                 from '@mathquis/modelx';
import { Connector }                  from '@mathquis/modelx';
import { ConnectorResult }            from '@mathquis/modelx';
import { ConnectorResults }           from '@mathquis/modelx';
import { InternalAxiosRequestConfig } from 'axios';
import { Model }                      from '@mathquis/modelx';
import _chunk                         from 'lodash/chunk';
import _get                           from 'lodash/get';
import _merge                         from 'lodash/merge';
import _set                           from 'lodash/set';
import authStore                      from '@/stores/authStore';
import qs                             from 'qs';
import { getLocale }                  from '@widesk-core/locale';

const FILTER_ID_ARRAY_LIMIT = 100;
const FILTER_URN_ARRAY_LIMIT = 100;
const MAX_ITEM_PER_PAGE = 100;  // MAX_ITEM_PER_PAGE doit être supérieur ou égale à FILTER_URN_ARRAY_LIMIT

export default class ApiConnector extends Connector {
	protected _authorizationHeaderName = 'X-Platform-Authorization';
	protected _useMergePatch = false;
	private readonly _client: AxiosInstance;

	// noinspection JSUnusedGlobalSymbols
	public resultTotalPath = 'hydra:totalItems';
	protected _memberPath = 'hydra:member';
	protected _totalItemsPath = 'hydra:totalItems';

	public constructor(options: any) {
		super(options);

		this._client = Axios.create({
			...options,
			headers: {
				'Accept': 'application/ld+json',
				'Content-Type': 'application/ld+json',
				'X-LOCALE': options.translations || getLocale() || 'fr',
				...options.headers,
			},
			responseType: 'json',
		});

		// Intercepteur de requêtes
		this._client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
			if (authStore.token) {
				if (authStore.refreshToken && authStore.isExpired()) {
					await authStore.refreshTokenAsync();
				}

				// Ajout du token JWT token à la requête
				config.headers[this._authorizationHeaderName] = 'Bearer ' + authStore.token;
			}

			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			config.metadata = { startTime: new Date() };

			return config;
		});

		// Intercepteur de réponses
		this._client.interceptors.response.use(response => {
			return response;
		}, error => {
			return Promise.reject(error);
		});
	}

	// Model methods
	public async fetch(model: Model, options: ConnectorFetchOptions = {}): Promise<ConnectorResult> {
		const customOptions = {
			method: 'get',
			...options,
			headers: { 'locale': (options.translations || ['fr', 'en']), ...options.headers },
		};

		const response = await this.request(model.path, customOptions);

		return new ConnectorResult(response?.data, response);
	}

	public async list<T extends Model>(collection: Collection<T>, options: ConnectorListOptions = {}) {
		const customOptions = {
			method: 'get',
			...options,
			headers: { 'locale': (options.translations || ['fr', 'en']), ...options.headers },
		};

		const response = await this.requestList(collection.path, customOptions);

		return new ConnectorResults(response?.data?.['hydra:member'], response);
	}

	public async save(model: ApiModel, options: ConnectorSaveOptions = {}) {
		const data = options.patchAttributes || model.untransformedAttributes as Record<string, string>;
		const method = options.method || (model.id ? (this._useMergePatch ? 'patch' : 'put') : 'post');
		const customOptions = {
			data: options.stringifyAttributes ? qs.stringify(data) : data,
			method,
			...options,
			headers: {
				'Content-Type': options.stringifyAttributes ?
					'application/x-www-form-urlencoded' :
					(method === 'patch' && this._useMergePatch ? 'application/merge-patch+json' : 'application/ld+json'),
				'locale': options.translations || (data.translations ? '*' : undefined),
				...options.headers,
			},
		};

		const path = options.path || model.path;
		const response: any = await this.request(path, customOptions);

		return new ConnectorResult(response?.data || {}, response);
	}

	public async destroy(model: ApiModel, options: Record<string, unknown> = {}) {
		const response = await this.request(model.path, { method: 'delete', ...options });

		return new ConnectorResult(model.attributes, response);
	}

	/**
	 * Concaténation de plusieurs réponses de liste pour ne récupérer qu'une seule réponse
	 * @param responses
	 * @private
	 */
	protected _concatListResponse(responses: (AxiosResponse)[]) {
		const newResponse = { ...responses[0] };
		_set(newResponse, `data.${this._memberPath}`, responses.map(r => _get(r, `data.${this._memberPath}`, [])).flat());
		return newResponse as AxiosResponse;
	}

	protected async requestList(path: string, options: Record<string, unknown> = {}): Promise<AxiosResponse | void> {
		const params: any = options.params || {};

		// On cherche un filtre "tableau d'urn" dans la liste qui dépasse la limite autorisée
		const filterUrnNameLimitExceeded = Object.keys(params).find(filterName => {
			const value = params[filterName];
			return filterName.endsWith('Urn') && Array.isArray(value) && value.length > FILTER_URN_ARRAY_LIMIT;
		});

		if (filterUrnNameLimitExceeded) {
			const urns = params[filterUrnNameLimitExceeded] as Urn[];

			// On découpe le tableau d'urn en plusieurs sous-tableaux
			const chunkUrnArrays = _chunk(urns, FILTER_URN_ARRAY_LIMIT);
			const responses = await Promise.all(chunkUrnArrays.map(urns => {
				return this.requestList(path, { options, params: { ...params, [filterUrnNameLimitExceeded]: urns } }) || {};
			}));

			return this._concatListResponse(responses.map(r => r || {} as AxiosResponse));
		}

		// On cherche si le filtre "id" dépasse la limite autorisée
		if (params['id'] && Array.isArray(params['id']) && params['id'].length > FILTER_ID_ARRAY_LIMIT) {

			// On découpe le tableau d'id en plusieurs sous-tableaux
			const chunkIdArrays = _chunk(params['id'], FILTER_ID_ARRAY_LIMIT);
			const responses = await Promise.all(chunkIdArrays.map(ids => {
				return this.requestList(path, { options, params: { ...params, id: ids } }) || {};
			}));

			return this._concatListResponse(responses.map(r => r || {} as AxiosResponse));
		}

		const hasPagination = typeof _get(options, 'params.itemsPerPage') !== 'undefined';

		const customOptions = _merge({ ...options }, {
			params: { itemsPerPage: _get(options, 'params.itemsPerPage', MAX_ITEM_PER_PAGE) }, // S'il n'y a pas d'ItemsPerPage, on en passe par défaut
		});

		const response = await this.request(path, customOptions);

		if (!hasPagination && response) { // Seulement pour les requêtes sans "itemsPerPage" (sans pagination)
			const total = _get(response, `data.${this._totalItemsPath}`, 0);
			const firstRequestItems = _get(response, `data.${this._memberPath}`, []);
			const items = [...firstRequestItems];

			if (items.length < total) { // Il y a moins d'items retournés que le total pour la requête
				const step = items.length;
				const pageQuantity = Math.ceil(total / step) - 1;

				// On requête toutes les autres pages à partir de la page 2
				const otherPagesResponses = await Promise.all(
					Array.from({ length: pageQuantity }, async (_, index) => {
						return this.request(path, { ...options, params: { ...params, itemsPerPage: step, page: index + 2, partial: true } });
					}),
				);

				// Construction d'une nouvelle response en fusionnant les responses de toutes les pages
				return this._concatListResponse([response, ...otherPagesResponses.map(r => r || {} as AxiosResponse)]);
			}
		}

		return response;
	}

	protected request(path: string, options: Record<string, unknown> = {}) {
		return this._client(path.replace(/\/$/, ''), { ...options }).catch(err => this.onRequestError(err));
	}

	protected async onRequestError(err: AxiosError) {
		if (Axios.isCancel(err)) {
			console.log(`Requête annulée`);
			return; // Si la requête est annulée on ne throw pas l'erreur
		}

		throw err;
	}
}
